// The warehouse's Points settings — one fetch, shared by every screen that
// scores and by the milestone celebration.
//
// Each scoring page used to fetch /settings/<warehouse> itself and pick out
// its own timer column. That was fine when three pages did it; with dispatch,
// picking, both drops and wipe joining in, a handheld would fire the same
// request five times on the way through a shift. This caches the promise per
// warehouse so the second caller waits on the first.
//
// A NULL target means the warehouse has not opted that flow into scoring, and
// is deliberately NOT replaced by a default: switching scoring on for a floor
// is a local decision, and a hardcoded fallback would make it for them.
import { useEffect, useState } from "react";
import { apiFetch } from "../../api/apiFetch";

/** Settings columns that carry a time target, keyed by the flow they score. */
export type PointsTargetKey =
    | "picking_timer_light_ms"
    | "picking_timer_pick_and_sort_ms"
    | "picking_timer_large_ms"
    | "drop_timer_shelf_ms"
    | "drop_timer_forecast_ms"
    | "dispatch_timer_light_ms"
    | "dispatch_timer_pick_and_sort_ms"
    | "dispatch_timer_large_ms"
    | "wipe_timer_ms";

export interface PointsSettings {
    targets: Partial<Record<PointsTargetKey, number | null>>;
    /** Milestone chime/fanfare. Default true — settings can silence a floor. */
    celebrationSound: boolean;
    /** How long after the first scan of the day (and after each open) the
     *  trophy modal can next be opened manually. */
    trophyOpenIntervalMinutes: number;
    /** How long the trophy modal stays up before closing itself. */
    trophyModalSeconds: number;
}

const DEFAULTS: PointsSettings = {
    targets: {},
    celebrationSound: true,
    trophyOpenIntervalMinutes: 120,
    trophyModalSeconds: 20,
};

const TARGET_KEYS: PointsTargetKey[] = [
    "picking_timer_light_ms",
    "picking_timer_pick_and_sort_ms",
    "picking_timer_large_ms",
    "drop_timer_shelf_ms",
    "drop_timer_forecast_ms",
    "dispatch_timer_light_ms",
    "dispatch_timer_pick_and_sort_ms",
    "dispatch_timer_large_ms",
    "wipe_timer_ms",
];

const intOr = (raw: unknown, fallback: number): number =>
    typeof raw === "number" && Number.isFinite(raw) && raw > 0 ? Math.round(raw) : fallback;

const parse = (data: any): PointsSettings => {
    const targets: Partial<Record<PointsTargetKey, number | null>> = {};
    for (const key of TARGET_KEYS) {
        const raw = data?.[key];
        targets[key] = typeof raw === "number" && raw > 0 ? raw : null;
    }
    return {
        targets,
        celebrationSound: data?.points_celebration_sound !== false,
        trophyOpenIntervalMinutes: intOr(data?.trophy_open_interval_minutes, DEFAULTS.trophyOpenIntervalMinutes),
        trophyModalSeconds: intOr(data?.trophy_modal_seconds, DEFAULTS.trophyModalSeconds),
    };
};

const cache = new Map<string, Promise<PointsSettings>>();

/** Cached fetch. A failed load resolves to defaults and is not cached, so the
 *  next screen retries rather than scoring being off for the rest of the day. */
export const loadPointsSettings = (warehouse: string): Promise<PointsSettings> => {
    const existing = cache.get(warehouse);
    if (existing) return existing;

    const promise = (async () => {
        try {
            const res = await apiFetch(`/settings/${encodeURIComponent(warehouse)}`, {});
            if (!res.ok) {
                cache.delete(warehouse);
                return DEFAULTS;
            }
            const parsed = parse(await res.json());
            // Refresh the sync-readable snapshots (event-handler consumers).
            lastKnownSound = parsed.celebrationSound;
            lastKnownGate = {
                intervalMinutes: parsed.trophyOpenIntervalMinutes,
                modalSeconds: parsed.trophyModalSeconds,
            };
            return parsed;
        } catch {
            cache.delete(warehouse);
            return DEFAULTS;
        }
    })();

    cache.set(warehouse, promise);
    return promise;
};

/** Drop the cached copy — called after Settings writes a new target. */
export const evictPointsSettings = (warehouse?: string) => {
    if (warehouse) cache.delete(warehouse);
    else cache.clear();
};

// Read synchronously by the milestone celebration, which fires on a window
// event rather than inside a component that can await. Seeded by the first
// loadPointsSettings() of the session and refreshed on every later one.
let lastKnownSound = true;
export const celebrationSoundEnabled = (): boolean => lastKnownSound;

// Same sync-read pattern for the trophy gate: the pill and the modal consult
// it from event handlers and render paths that cannot await.
let lastKnownGate = {
    intervalMinutes: DEFAULTS.trophyOpenIntervalMinutes,
    modalSeconds: DEFAULTS.trophyModalSeconds,
};
export const trophyGateConfig = () => lastKnownGate;
export const primeCelebrationSound = (warehouse: string | undefined) => {
    if (!warehouse) return;
    void loadPointsSettings(warehouse).then(s => { lastKnownSound = s.celebrationSound; });
};

export const usePointsSettings = (warehouse: string | undefined): PointsSettings | null => {
    const [settings, setSettings] = useState<PointsSettings | null>(null);
    useEffect(() => {
        if (!warehouse) { setSettings(null); return; }
        let alive = true;
        void loadPointsSettings(warehouse).then(s => { if (alive) setSettings(s); });
        return () => { alive = false; };
    }, [warehouse]);
    return settings;
};

/**
 * The target for one flow, or null while loading / when the warehouse hasn't
 * set one. Callers must treat null as "don't score", not "use 6 seconds".
 */
export const usePointsTarget = (
    warehouse: string | undefined,
    key: PointsTargetKey | null,
): number | null => {
    const settings = usePointsSettings(warehouse);
    if (!settings || !key) return null;
    return settings.targets[key] ?? null;
};
